/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import { gnoment } from 'shared-ui/gnoment';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { FlexRow, FlexCol, Heading, useAssetImages } from 'sarsaparilla';
import { HeroImageGallery } from 'shared-ui/hero-image-gallery';
import { getUnitCardinality } from 'ui-permit/src/utils/utilsNew';
import { FacilityProps, PassTypeProps, ReservationProps } from '../props';
import { formatPrice, getSeasonDates } from '../utils';
import { getDuration, validToDate } from '../utils/duration_utils';
import LEGACY_CostsTable from './LEGACY_CostsTable';
import { isDailyPassType } from '../utils/date_time_utils';

function buildCosts(passType, reservation) {
    const daySegmentGroups = get(passType, ['features', 'day-segment', 'groups'], {});
    const daySegmentAttendees = get(
        reservation,
        ['features', 'day-segment', 'groups'],
        {}
    );
    const groups = get(passType, ['features', 'attendee-info'], daySegmentGroups);
    const attendees = get(
        reservation,
        ['features', 'attendee-info'],
        daySegmentAttendees
    );
    const attendeeInfo = [];
    const costs = [
        { title: `${passType.name}`, quantity: 'x 1', value: reservation.price },
    ];
    if (reservation.fees) {
        costs.push({
            title: `Service Fee`,
            quantity: 'x 1',
            value: reservation.fees,
        });
    }
    const total = { title: 'Subtotal', value: reservation.total };
    let allowedToBuy = true;

    Object.entries(groups).forEach(([key, group]) => {
        const count = attendees[key];

        if (!count) {
            return;
        }

        if (group.attendee_max > -1) {
            if (group.price_per_attendee && count > group.attendee_max) {
                allowedToBuy = false;
            }
        }

        attendeeInfo.push(
            <span key={key} className="parkpass-reservation-summary-attendee-info">
                {`${count} ${getUnitCardinality(count, key.replace(/s$|ren$/, ''), key)} `}
            </span>
        );

        let cost;

        cost = Math.max(group.price_min, count * group.price_per_attendee);
        if (group.price_max > 0) {
            cost = Math.min(group.price_max, cost);
        }

        if (cost) {
            costs.push({
                title: passType.name,
                quantity: `x 1`,
                description: passType.description,
                value: cost,
            });

            total.value += cost;
        }
    });

    return { allowedToBuy, attendeeInfo, costs, total };
}

//Stable empty gallery reference to prevent infinite loop
const EMPTY_GALLERY = [];

/**
 * NOTE: This component is used by ui-widget. We will need to check ui-widget whenever we do a change here
 */
function LEGACY_ReservationSummary({ facility, passTypes, reservations, children }) {
    const totalCost = { title: 'Total Cost', value: 0 };
    let hasErrorInPass = false;
    const { heroImage, status: heroImageStatus } = useAssetImages({
        assetId: facility?.asset_id || facility?.id,
    });

    return (
        <div className="aside parkpass-reservation-summary">
            <div className="parkpass-reservation-image-wrap">
                <HeroImageGallery
                    heroImage={heroImage}
                    galleryImages={EMPTY_GALLERY}
                    status={heroImageStatus}
                    className="parkpass-reservation-hero-image-gallery"
                />
            </div>

            <div className="parkpass-reservation-summary-body-wrap">
                <Heading className="rec-sr-only" headingLevel={2}>
                    Cost Summary
                </Heading>
                {!reservations.length && (
                    <FlexRow
                        hasGutters={false}
                        className="parkpass-reservation-summary-subhead-wrap"
                    >
                        <FlexCol>
                            <div className="parkpass-reservation-summary-title">
                                Site Pass
                            </div>
                            <div className="parkpass-reservation-summary-name">
                                {facility.name}
                            </div>
                        </FlexCol>
                    </FlexRow>
                )}

                {reservations.map((reservation, number) => {
                    const passType = passTypes.find(
                        ({ id }) => id === reservation.pass_type_id
                    );
                    if (!passType) {
                        return null;
                    }
                    const { allowedToBuy, attendeeInfo, costs, total } = buildCosts(
                        passType,
                        reservation
                    );
                    totalCost.value += total.value;
                    LEGACY_ReservationSummary.total = formatPrice(totalCost.value);

                    const isSeasonal = ['season', 'seasonal'].includes(passType.duration);
                    const DATE_FORMAT = 'ddd, ll';
                    const [seasonStart] = getSeasonDates(passType.periods);
                    let [, seasonEnd] = getSeasonDates(passType.periods);
                    const startDateMoment = gnoment(
                        isSeasonal
                            ? seasonStart
                            : reservation?.pass_info?.features['start-date']
                    );

                    let startDate = startDateMoment.format(DATE_FORMAT);
                    let endDate = validToDate(startDateMoment, passType).format(
                        DATE_FORMAT
                    );
                    // Some durations are flexible/user-customizable, so we have to calculate them instead of using durations.
                    const duration = getDuration(passType.duration);
                    const canModifyEndDate =
                        passType.features['end-date'] ||
                        passType.features['day-segment'] ||
                        passType.duration === 'segment';
                    if (canModifyEndDate) {
                        const end = reservation.pass_info.features['end-date'];
                        duration.days = gnoment(end)
                            .add(2, 'days')
                            .diff(gnoment(), 'days');
                    }
                    if (isSeasonal) {
                        // For seasonal pricing, we have to get the end date ourselves.
                        duration.days = gnoment(seasonEnd)
                            .add(2, 'days')
                            .diff(gnoment(), 'days');

                        endDate = gnoment(seasonEnd).format(DATE_FORMAT);
                    }

                    if (!gnoment(seasonEnd).isValid()) {
                        const [year, month, day] = seasonEnd?.split('-') ?? [
                            undefined,
                            undefined,
                            undefined,
                        ];
                        const adjustedYear = parseInt(year, 10) + 1;
                        seasonEnd = `${adjustedYear}-${month}-${day}`;
                        endDate = gnoment(seasonEnd).format(DATE_FORMAT);
                    }

                    const isLimitedToSeason =
                        passType.duration.indexOf('limit-to-season') !== -1;

                    if (isLimitedToSeason) {
                        const days = duration.days - 1;
                        const endValue = gnoment(startDateMoment).add(days, 'd');
                        const isAfterSeasonEnd = endValue.isAfter(seasonEnd, 'day');
                        endDate = endValue.format(DATE_FORMAT);

                        if (isAfterSeasonEnd) {
                            endDate = gnoment(seasonEnd).format(DATE_FORMAT);
                        }
                    }

                    const isEmptyDailyPassDate =
                        isDailyPassType(passType?.duration) &&
                        isEmpty(reservation?.pass_info?.features['start-date']);

                    if (isEmptyDailyPassDate) {
                        startDate = 'Select Start date';
                        endDate = 'Select Start date';
                    }

                    hasErrorInPass = hasErrorInPass || !allowedToBuy;

                    return (
                        <div
                            key={reservation.id}
                            className="parkpass-reservation-summary-item"
                        >
                            <FlexRow
                                hasGutters={false}
                                className="parkpass-reservation-summary-subhead-wrap"
                            >
                                <FlexCol>
                                    <Heading
                                        headingLevel={3}
                                        appearance="h6"
                                        className="parkpass-reservation-summary-title"
                                    >
                                        {number + 1}. {passType.name}
                                    </Heading>
                                    <div className="parkpass-reservation-summary-name">
                                        {facility.name}
                                    </div>
                                </FlexCol>
                            </FlexRow>

                            <FlexRow
                                hasGutters={false}
                                className="parkpass-reservation-summary-item-body"
                            >
                                <FlexCol
                                    xs={6}
                                    md={12}
                                    xl={6}
                                    className="parkpass-reservation-summary-dates"
                                >
                                    <FlexRow xs={12}>
                                        <FlexCol>Start Date</FlexCol>
                                        <FlexCol xs={12}>
                                            <strong>{startDate}</strong>
                                        </FlexCol>
                                    </FlexRow>
                                </FlexCol>

                                <FlexCol xs={6} md={12} xl={6}>
                                    <FlexRow>
                                        <FlexCol xs={12}>Valid Through</FlexCol>
                                        <FlexCol xs={12}>
                                            <strong>{endDate}</strong>
                                        </FlexCol>
                                    </FlexRow>
                                </FlexCol>
                            </FlexRow>

                            {allowedToBuy ? (
                                <div>
                                    <LEGACY_CostsTable
                                        lines={costs}
                                        total={total}
                                        attendeeInfo={attendeeInfo}
                                    />
                                </div>
                            ) : (
                                <strong className="rec-text-danger">
                                    Your selections are outside the bounds for this pass
                                    type. Please make a different selection.
                                </strong>
                            )}
                        </div>
                    );
                })}

                <LEGACY_CostsTable
                    lines={[]}
                    total={totalCost}
                    showNoLinesText={!reservations.length}
                />

                {!hasErrorInPass && children}
            </div>
        </div>
    );
}

LEGACY_ReservationSummary.propTypes = {
    children: PropTypes.node,
    facility: FacilityProps.isRequired,
    passTypes: PropTypes.arrayOf(PassTypeProps).isRequired,
    reservations: PropTypes.arrayOf(ReservationProps).isRequired,
};

LEGACY_ReservationSummary.total = '$0.00';

// Preserves the previous `shouldComponentUpdate` behavior: only re-render when every
// reservation has a price or is a daily pass type. React.memo skips the render when the
// comparator returns true, so the condition is inverted here.
export default React.memo(
    LEGACY_ReservationSummary,
    (prevProps, nextProps) =>
        !nextProps.reservations.every(
            (res) => res.has_price || isDailyPassType(res?.pass_info?.duration)
        )
);

// cspell:ignore parkpass
